// Core
import React, { useCallback, useEffect, useState } from 'react';

// Libraries
import { Trans, useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useTheme } from 'styled-components';

// Hooks
import { useBreakpoint } from '../../../../../../hooks/useBreakpoint';

// Plugins
import {
  lidStatusToTagType,
  lidStatusToText,
  permissiveStatusToIndicatorStatus,
  runningStatusToIndicatorStatus,
} from '../../../../../../plugins/crusher';
import { timestampToTimeAgo, DASHBOARD_NULLISH_TEXT } from '../../../../../../plugins/general';
import { RequestMethod, requestVantageApi } from '../../../../../../plugins/request';

// Store
import { getUser, getCustomer } from '../../../../../../store/session.store';

// Types
import {
  CrusherRunningSStatus,
  LidStatusItem,
  LidStatusResponse,
} from '../../../../../../types/crusher';
import {
  User,
  Customer,
  UserType,
  SortOrder,
} from '../../../../../../types/global';
import { Theme } from '../../../../../../types/theme';

// Components
import { Col } from '../../../../../../layout/elements/Col';
import { Loader } from '../../../../../../layout/elements/Loader';
import {
  Paragraph,
  ParagraphSize,
  ParagraphWeight,
} from '../../../../../../layout/elements/Paragraph';
import { Row } from '../../../../../../layout/elements/Row';
import {
  StatusIndicator,
  StatusIndicatorSize,
} from '../../../../../../layout/elements/StatusIndicator';
import { Table, TableProps } from '../../../../../../layout/elements/Table';
import { TableColumnProps } from '../../../../../../layout/elements/Table/Table.types';
import { Tag } from '../../../../../../layout/elements/Tag';
import { Tooltip, TooltipPlacement } from '../../../../../../layout/elements/Tooltip';
import { PaginationFooter } from '../../../../../../layout/elements/PaginationFooter';

// View
import {
  FirstPermissivesListContainer,
  LastOpenedOnContainer,
  LastOpenedOnTime,
  LidPermissivesTitle,
  LidStatusContainer,
  LidStatusPermissivesContainer,
  LidStatusTableContainer,
  LidStatusWrapper,
  PermissiveItem,
  PermissivesListContainer,
  PermissiveTitleContainer,
} from './LidStatus.style';
import { LidStatusProps } from './LidStatus.types';

// Constants
const POLLING_INTERVAL_MS: number = 60000;
const DEFAULT_PAGE_SIZE: number = 10;

type LidStatusState = {
  data: LidStatusItem[];
  isLoading: boolean;
  isPolling: boolean;
  page: number;
  pageSize: number;
  sortBy: string;
  sortOrder: SortOrder;
  totalItems: number;
};

function LidStatus({
  unitData,
  edgeStatus,
  equipmentUnitId,
  lastLidOpenedAt,
  testId,
  className,
}: LidStatusProps): JSX.Element {
  const { t } = useTranslation();
  const { isXs } = useBreakpoint();
  const theme: Theme = useTheme();
  const user: User | null = getUser();
  const customer: Customer | null = getCustomer();

  // State for lid stats table
  const [lidStatus, setLidStatus] = useState<LidStatusState>({
    data: [],
    isLoading: true,
    isPolling: false,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    sortBy: 'date',
    sortOrder: SortOrder.descend,
    totalItems: 0,
  });

  // Fetch lid stats from backend
  const fetchLidStatus = useCallback(async (
    page: number,
    pageSize: number,
    sortBy: string,
    sortOrder: string,
    isPolling: boolean = false,
  ): Promise<void> => {
    try {
      // Only show loader if it's not a polling request
      if (!isPolling) {
        setLidStatus((prev: LidStatusState) => ({ ...prev, isLoading: true }));
      }

      const response: LidStatusResponse = await requestVantageApi.equipmentUnitDataMonitoring({
        method: RequestMethod.GET,
        path: `/crusher/vsi/lid_status/${equipmentUnitId}`,
        params: (user?.type === UserType.admin || user?.type === UserType.service)
          ? {
            page,
            pageSize,
            sortBy,
            sortOrder,
            idCustomer: customer?.id,
          }
          : {
            page,
            pageSize,
            sortBy,
            sortOrder,
          },
      });

      setLidStatus((prev: LidStatusState) => ({
        ...prev,
        data: response?.lidStatus ?? [],
        totalItems: response?.totalItems ?? 0,
        page: response?.page ?? prev.page,
        pageSize: response?.pageSize ?? prev.pageSize,
        isLoading: false,
        isPolling,
      }));
    } catch {
      setLidStatus((prev: LidStatusState) => ({
        ...prev,
        data: [],
        isLoading: false,
        isPolling,
      }));
    }
  }, [equipmentUnitId, user?.type, customer?.id]);

  /* ***********************************************************************************************
  **************************************** COMPONENT HANDLING **************************************
  *********************************************************************************************** */

  // Handle page change
  const handlePageChange = useCallback((page: number, pageSize: number): void => {
    setLidStatus((prev: LidStatusState) => {
      // Fetch immediately when user changes page or pageSize
      fetchLidStatus(page, pageSize, prev.sortBy, prev.sortOrder, false);

      return {
        ...prev,
        page,
        pageSize,
      };
    });
  }, [fetchLidStatus]);

  // Handle table sort change via Ant Design onChange
  const handleTableChange = useCallback((_pagination: any, _filters: any, sorter: any): void => {
    if (!sorter || !sorter.columnKey) return;

    setLidStatus((prev: LidStatusState) => {
      let newSortOrder: SortOrder;

      if (sorter.order === SortOrder.ascend) {
        newSortOrder = SortOrder.ascend;
      } else if (sorter.order === SortOrder.descend) {
        newSortOrder = SortOrder.descend;
      } else {
        newSortOrder = prev.sortOrder === SortOrder.descend ? SortOrder.ascend : SortOrder.descend;
      }

      // Fetch immediately when user changes sort
      fetchLidStatus(1, prev.pageSize, sorter.columnKey, newSortOrder, false);

      return {
        ...prev,
        sortBy: sorter.columnKey,
        sortOrder: newSortOrder,
        page: 1,
      };
    });
  }, [fetchLidStatus]);

  // Table columns
  const columns: TableColumnProps[] = [
    {
      key: 'date',
      dataIndex: 'date',
      title: `${t('view.crusher.vsi.drawers.lidStatus.columnDate')}`,
      sorter: true,
      sortOrder: lidStatus.sortBy === 'date' ? lidStatus.sortOrder : undefined,
      render: (text: string): string => {
        if (!text) return DASHBOARD_NULLISH_TEXT;
        const date = new Date(text);
        const utcDate = new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
        return dayjs(utcDate).format('ll');
      },
    },
    {
      key: 'counter',
      dataIndex: 'counter',
      title: `${t('view.crusher.vsi.drawers.lidStatus.columnCounter')}`,
      sorter: true,
      sortOrder: lidStatus.sortBy === 'counter' ? lidStatus.sortOrder : undefined,
    },
    {
      key: 'interval',
      dataIndex: 'interval',
      title: `${t('view.crusher.vsi.drawers.lidStatus.columnInterval')}`,
      sorter: true,
      sortOrder: lidStatus.sortBy === 'interval' ? lidStatus.sortOrder : undefined,
      render: (_text: string, record: any): string => {
        if (record.interval === null) return DASHBOARD_NULLISH_TEXT;
        return `${record.interval} ${t('view.crusher.vsi.drawers.lidStatus.days')}`;
      },
    },
  ];

  // Format rows with keys for Ant Design
  const rows: TableProps['rows'] = lidStatus.data.map((item: LidStatusItem, index: number) => ({
    ...item,
    key: `${item.date}-${index}`,
  }));

  // Last opened on formatting
  const lastOpenedTooltipText: string = lastLidOpenedAt
    ? dayjs(lastLidOpenedAt).format('LLL')
    : '';
  const lastOpenedRelativeText: string = lastLidOpenedAt
    ? timestampToTimeAgo(lastLidOpenedAt)
    : t('view.crusher.vsi.lidStatus.noOpeningsRecorded');

  const renderLidPermissive = (
    permissiveName: string,
    permissive: boolean | null | undefined,
  ): JSX.Element => (
    <PermissiveItem
      data-testid="lid-status-drawer-permissive-item"
      status={permissiveStatusToIndicatorStatus(
        permissive,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      )}
    >
      <StatusIndicator
        text={t(`view.crusher.vsi.drawers.lidStatus.${permissiveName}`)}
        status={permissiveStatusToIndicatorStatus(
          permissive,
          edgeStatus ? edgeStatus.edgeStatus : null,
          unitData?.timestamp,
        )}
      />
    </PermissiveItem>
  );

  const renderRunningStatusPermissive = (
    permissiveName: string,
    permissive: CrusherRunningSStatus | null | undefined,
  ): JSX.Element => (
    <PermissiveItem
      data-testid="lid-status-drawer-permissive-item"
      status={runningStatusToIndicatorStatus(
        permissive,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      )}
    >
      <StatusIndicator
        text={t(`view.crusher.vsi.drawers.lidStatus.${permissiveName}`)}
        status={runningStatusToIndicatorStatus(
          permissive,
          edgeStatus ? edgeStatus.edgeStatus : null,
          unitData?.timestamp,
        )}
      />
    </PermissiveItem>
  );

  // Initial fetch + polling (only runs once on mount)
  useEffect((): (() => void) => {
    fetchLidStatus(
      lidStatus.page,
      lidStatus.pageSize,
      lidStatus.sortBy,
      lidStatus.sortOrder,
      false,
    );

    // Setup polling interval
    const intervalId: NodeJS.Timeout = setInterval((): void => {
      // Use current state values for polling, with isPolling=true to avoid showing loader
      setLidStatus((currentState: LidStatusState) => {
        fetchLidStatus(
          currentState.page,
          currentState.pageSize,
          currentState.sortBy,
          currentState.sortOrder,
          true,
        );
        return currentState;
      });
    }, POLLING_INTERVAL_MS);

    return (): void => {
      clearInterval(intervalId);
    };
  }, [fetchLidStatus, lidStatus.page, lidStatus.pageSize, lidStatus.sortBy, lidStatus.sortOrder]);

  return (
    <div
      data-testid={testId}
      className={className}
    >
      {/* Lid Status + Tag + Last Opened On */}
      <Row>
        <Col>
          <LidStatusWrapper>
            <LidStatusContainer
              data-testid="lid-status-drawer-lid-status-container"
            >
              <Paragraph size={ParagraphSize.md} weight={ParagraphWeight.bold}>
                {t('view.crusher.vsi.lidStatus.title')}
              </Paragraph>
              <Tag
                type={lidStatusToTagType(
                  unitData?.lidOpenStatus,
                  edgeStatus ? edgeStatus.edgeStatus : null,
                  unitData?.timestamp,
                )}
                text={lidStatusToText(unitData?.lidOpenStatus)}
              />
            </LidStatusContainer>
            <LastOpenedOnContainer>
              <Tooltip text={lastOpenedTooltipText} placement={TooltipPlacement.bottom}>
                <Paragraph
                  size={ParagraphSize.sm}
                  color={theme.color.crusherDashboard.lidStatusDrawer.lastOpenedOnTextColor}
                >
                  <Trans
                    i18nKey="view.crusher.vsi.lidStatus.lastOpenedOn"
                    values={{ lastOpenedOn: lastOpenedRelativeText }}
                    components={{ lastOpenedOnTime: <LastOpenedOnTime /> }}
                  />
                </Paragraph>
              </Tooltip>
            </LastOpenedOnContainer>
          </LidStatusWrapper>
        </Col>
      </Row>

      {/* Lid Permissives Section */}
      <Row>
        <Col>
          <LidPermissivesTitle
            data-testid="lid-status-drawer-lid-permissives-title"
          >
            <Paragraph size={ParagraphSize.md} weight={ParagraphWeight.bold}>
              {t('view.crusher.vsi.drawers.lidStatus.lidPermissives')}
            </Paragraph>
          </LidPermissivesTitle>
        </Col>
      </Row>

      <Row>
        <Col>
          <PermissiveTitleContainer
            data-testid="lid-status-drawer-permissive-title-container"
          >
            <StatusIndicator
              text={t('view.crusher.vsi.drawers.lidStatus.lidMovementPermissive')}
              size={isXs ? StatusIndicatorSize.sm : StatusIndicatorSize.lg}
              status={permissiveStatusToIndicatorStatus(
                unitData?.lidMovementPermissive,
                edgeStatus ? edgeStatus.edgeStatus : null,
                unitData?.timestamp,
              )}
            />
          </PermissiveTitleContainer>
        </Col>
        <LidStatusPermissivesContainer
          data-testid="lid-status-drawer-permissives-list-container"
        >
          <Row>
            <Col xs={24} xl={12}>
              <FirstPermissivesListContainer>
                {renderRunningStatusPermissive('crusherMotorStatus', unitData?.crusherMotorStatus)}
                {renderLidPermissive('lidLiftCoastLockout', unitData?.lidLiftCoastLockout)}
                {renderLidPermissive('mccLocalMode', unitData?.mccLocalMode)}
              </FirstPermissivesListContainer>
            </Col>
            <Col xs={24} xl={12}>
              <PermissivesListContainer>
                {renderLidPermissive('lidLimitSwitch', unitData?.lidLimitSwitch)}
                {renderLidPermissive('enableHMI', unitData?.enableHMI)}
              </PermissivesListContainer>
            </Col>
          </Row>
        </LidStatusPermissivesContainer>
      </Row>

      {/* Lid Stats History Table */}
      <Row>
        <Col>
          <LidStatusTableContainer>
            <Paragraph size={ParagraphSize.md} weight={ParagraphWeight.bold}>
              {t('view.crusher.vsi.drawers.lidStatus.historyTitle')}
            </Paragraph>
            {lidStatus.isLoading
              ? (
                <Loader />
              ) : (
                <>
                  <Table
                    columns={columns}
                    rows={rows}
                    pagination={false}
                    onChange={handleTableChange}
                    testId="lid-stats-table"
                  />
                  <PaginationFooter
                    fixed={false}
                    currentPage={lidStatus.page}
                    onPageChange={handlePageChange}
                    totalItems={lidStatus.totalItems}
                    showPageSizeSelector={false}
                  />
                </>
              )}
          </LidStatusTableContainer>
        </Col>
      </Row>
    </div>
  );
}

export { LidStatus };
